import { DocumentReference } from 'firebase/firestore';
import { type Effect } from './effect';
import { type FeatureOption, FeatureType, propertyTypeToFeatureType } from './feature';

export enum PropertyVisibility {
	Public = 0,
	Beta = 1,
	Hidden = 2,
	// Owner has requested Beta promotion; awaiting admin approval on the
	// Nucleus Pending page. Visible to the owner and admins only.
	PendingBeta = 3
}

export enum PropertyType {
	armor = 'armor',
	biology = 'biology',
	core = 'core',
	coreAbility = 'coreAbility',
	corp = 'corp',
	creature = 'creature',
	damage = 'damage',
	feat = 'feat',
	homeWorld = 'homeWorld',
	item = 'item',
	knack = 'knack',
	legacy = 'legacy',
	legacyItem = 'legacyItem',
	natural = 'natural',
	origin = 'origin',
	quirk = 'quirk',
	senses = 'senses',
	skill = 'skill',
	skillBoon = 'skillBoon',
	skillTrick = 'skillTrick',
	stats = 'stats',
	talent = 'talent',
	weapon = 'weapon',
	weaponExpansion = 'weaponExpansion',
	weaponTrick = 'weaponTrick',
	// Foundry token-effect properties. Not gameplay stat blocks — they carry
	// a tokenLightSource / tokenVisionSource effect the Foundry system reads
	// on equip. `lightSource` emits global light; `visionSource` grants the
	// wearer personal sight (IR / night vision). Both also carry the `vision`
	// tag so "everything that affects Foundry vision" is findable together.
	lightSource = 'lightSource',
	visionSource = 'visionSource',
}

export enum durationApplication {
	na,
	stacks,
	overrides
}

export enum durationType {
	none,
	nextroll,
	actions,
	turns,
	minutes,
	hours,
	days,
	never
}


export const getPropertyIconByType = (type: PropertyType): string => {
	switch (type) {
		case PropertyType.origin:
			return 'img:/properties/origin.svg';
		case PropertyType.legacy:
			return 'img:/properties/legacy.svg';
		case PropertyType.legacyItem:
			return 'img:/properties/legacy.svg';
		case PropertyType.knack:
			return 'img:/properties/knack.svg';
		case PropertyType.skillBoon:
			return 'img:/properties/skillBoon.svg';
		case PropertyType.homeWorld:
			return 'img:/properties/homeWorld.svg';
		case PropertyType.stats:
			return 'img:/properties/stats.svg';
		case PropertyType.core:
			return 'img:/properties/core.svg';
		case PropertyType.corp:
			return 'img:/properties/corp.svg';
		case PropertyType.skillTrick:
			return 'img:/properties/skillBoon.svg';
		case PropertyType.weaponTrick:
			return 'img:/properties/skillBoon.svg';
		case PropertyType.natural:
			return 'img:/properties/natural.svg';
		case PropertyType.damage:
			return 'img:/svg/defense-satellite.svg';
		case PropertyType.creature:
			return 'pets';
		default:
			return '❓';
	}
};

export interface Property {
	id: string;
	ref: DocumentReference | undefined;
	name: string;
	tag: string;
	description: string;
	credits: number;
	actionCost: number;
	type: PropertyType;
	visibility: PropertyVisibility;
	/** UID of the owner. New content is owned by its author; once made Public
	 *  ownership transfers to the SYSTEM_OWNER_UID system account. Optional for
	 *  backward compatibility with pre-ownership documents (treated as system-
	 *  owned / admin-managed when absent). */
	ownerId?: string;
	imageURL: string;
	docURL: string;
	durationApplication: durationApplication;
	durationType: durationType;
	duration: number;
	requires: string[];
	effectRefs: DocumentReference[];
	effects: Effect[];
	/** Optional free-form capability tags (e.g. ['vision'], ['augment','aionic']).
	 *  Sparsely populated; used for grouping/finding properties across types. */
	tags?: string[];
	baseStats?: Record<string, number>;
	skillGroup?: string;
	/** Bundle-hydrated: string IDs for lazy effect resolution (replaces effectRefs) */
	_effectRefIds?: string[];
}

export interface RequiresContext {
	getSkillSP?: (skillId: string) => number;
}

/**
 * Check if a set of character tags satisfies a property's requires field.
 *
 * requires is a flat string array where each entry is an AND group
 * with tags joined by '&&'. The outer array is OR.
 *
 * Examples:
 *   []                                  — no restrictions (always passes)
 *   ["human"]                           — needs 'human' tag
 *   ["human", "cyborg"]                 — needs 'human' OR 'cyborg'
 *   ["bohec&&bohec-resistant-carapace"]  — needs 'bohec' AND 'bohec-resistant-carapace'
 *   ["human&&rank3", "cyborg&&rank1"]   — (human AND rank3) OR (cyborg AND rank1)
 *   ["skill-engineering-rigging>=5"]    — needs 5+ SP in skill-engineering-rigging
 */
export function meetsRequires(
	requires: string[] | undefined,
	characterTags: Set<string>,
	context?: RequiresContext
): boolean {
	if (!requires || requires.length === 0) return true;
	return requires.some(andGroup => {
		return andGroup.split('&&').every(part => {
			const thresholdMatch = part.match(/^(.+?)>=(\d+)$/);
			if (thresholdMatch) {
				if (!context?.getSkillSP) return false;
				return context.getSkillSP(thresholdMatch[1]) >= parseInt(thresholdMatch[2]);
			}
			return characterTags.has(part);
		});
	});
}

export const getDefaultProperty = (): Property => {
	return {
		id: '-1',
		ref: undefined,
		name: '',
		tag: '',
		description: '',
		credits: 0,
		actionCost: 0,
		type: PropertyType.item,
		visibility: PropertyVisibility.Public,
		imageURL: '',
		docURL: '',
		requires: [] as string[],
		effectRefs: [],
		effects: [],
		durationApplication: durationApplication.na,
		durationType: durationType.none,
		duration: 0
	};
};

export const property2FeatureOption = (property: Property, qty?: number): FeatureOption => {
    const option: FeatureOption = {
        id: property.id,
        name: property.name,
        type: propertyTypeToFeatureType(property.type) || FeatureType.corp,
        imageURL: property.imageURL,
        docURL: property.docURL,
        tag: property.tag,
        description: property.description,
        skillGroup: property.skillGroup,
    };
    if (qty !== undefined) {
        option.qty = qty;
    }
    return option;
};
